import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Commande, FicheConseil, FicheConseilState, InvestisseurType } from '@lib/models/generated/graphql';
import { OuiDialogService } from 'omnium-ui/dialog';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { OuiStep, OuiStepper } from 'omnium-ui/stepper';
import { Subscription } from 'rxjs';
import { DeprecationWarningDialogComponent } from 'src/app/components/declare-fiche-conseil/deprecation-warning-dialog/deprecation-warning-dialog.component';
import { ExigencesStepComponent } from 'src/app/components/declare-fiche-conseil/steps/exigences-step/exigences-step.component';
import { InvestisseurStepComponent } from 'src/app/components/declare-fiche-conseil/steps/investisseur-step/investisseur-step.component';
import { PrecisionComplementaireStepComponent } from 'src/app/components/declare-fiche-conseil/steps/precision-complementaire-step/precision-complementaire-step.component';
import { ProjetInvestissementStepComponent } from 'src/app/components/declare-fiche-conseil/steps/projet-investissement-step/projet-investissement.component';
import { StepConnaissanceClientComponent } from 'src/app/components/declare-fiche-conseil/steps/step-connaissance-client/step-connaissance-client.component';
import { ConseilConfigService } from 'src/app/services/conseil-config.service';
import { FicheConseilService } from 'src/app/services/fiche-conseil.service';
import { ContratStepComponent } from './steps/contrat-step/contrat-step.component';
import { PERSONNE_PHYSIQUE_FEMALE_GENDER_VALUE } from '@lib/models/graphqlData';

export enum StepEnum {
  CONTRAT = 'Contrat',
  INVESTISSEUR = 'Investisseur(s)',
  CONNAISSANCE = 'Connaissance client',
  EXIGENCES = 'Exigences et besoins du projet',
  INVESTISSEMENT = 'Projet investissement',
  PRECISIONS = 'Précisions complémentaires',
}

type FicheStep = OuiStep & { ficheStates: FicheConseilState[]; stepEnum: StepEnum };

@Component({
  selector: 'app-declare-fiche-conseil',
  templateUrl: './declare-fiche-conseil.component.html',
  styleUrls: ['./declare-fiche-conseil.component.scss'],
})
export class DeclareFicheConseilComponent {
  stepperTitle: string = 'Fiche Conseil';
  operationTypeLabel: string = 'OperationType';
  enveloppeLabel: string = 'Enveloppe';
  InvestisseurTypeLabel: string = 'Investisseur Type';
  @ViewChild('stepper') stepper?: OuiStepper;

  @ViewChild('contratStepComponent') contratStepComponent?: ContratStepComponent;
  @ViewChild('investisseurStepComponent') investisseurStepComponent?: InvestisseurStepComponent;
  @ViewChild('stepConnaissanceClientComponent') stepConnaissanceClientComponent?: StepConnaissanceClientComponent;
  @ViewChild('exigencesStepComponent') exigencesStepComponent?: ExigencesStepComponent;
  @ViewChild('projetInvestissementComponent') projetStepComponent?: ProjetInvestissementStepComponent;
  @ViewChild('precisionComplementaireStep') precisionComplementaireStep?: PrecisionComplementaireStepComponent;

  stepEnum = StepEnum;

  stepToFichesState: Map<StepEnum, FicheConseilState[]> = new Map<StepEnum, FicheConseilState[]>([
    [StepEnum.CONTRAT, [FicheConseilState.DraftCreated, FicheConseilState.DraftInvestisseurSelected]],
    [StepEnum.INVESTISSEUR, [FicheConseilState.DraftCreated, FicheConseilState.DraftInvestisseurSelected]],
    [StepEnum.CONNAISSANCE, [FicheConseilState.DraftInvestisseurValidated]],
    [StepEnum.EXIGENCES, [FicheConseilState.DraftKycValidated]],
    [StepEnum.INVESTISSEMENT, [FicheConseilState.DraftRequirementsAndNeedsValidated]],
    [StepEnum.PRECISIONS, [FicheConseilState.DraftInvestmentProjectValidated, FicheConseilState.ConseilDone]],
  ]);

  steps: FicheStep[] = Object.values(StepEnum).map(step => {
    return {
      title: step,
      state: 'todo',
      iconColor: 'primary',
      ficheStates: this.stepToFichesState.get(step) ?? [],
      stepEnum: step,
    };
  });

  currentStep?: FicheStep;
  isLoading: boolean = false;
  currentFicheConseil: FicheConseil | null = null;
  ficheConseilSuppressionDelay: number = 90;

  deprecationWarningSub: Subscription;
  get isLastStep(): boolean {
    return this.stepper?.stepIndex === this.steps.length - 1;
  }

  get ctaLabel(): string {
    if (this.isLastStep) {
      return 'Terminer et générer la fiche conseil';
    }
    return 'Etape suivante';
  }

  get previousLabel(): string {
    if (this.currentStep === this.steps[0]) {
      return 'Retour';
    }
    return 'Etape précédente';
  }
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    protected ficheConseilService: FicheConseilService,
    private conseilConfigService: ConseilConfigService,
    private snackbarService: OuiSnackbarService,
    private ouiDialogService: OuiDialogService
  ) {}

  ngOnInit(): void {
    const path = this.route.snapshot.url[0].path;
    // set stepper according to path (souscription or acte de gestion)
    if (path == 'acte') {
      this.steps = this.steps.filter(step => step.stepEnum !== StepEnum.INVESTISSEUR);
    } else if (path == 'fiche') {
      this.steps = this.steps.filter(step => step.stepEnum !== StepEnum.CONTRAT);
      // keep all steps
    } else {
      this.stepperTitle = 'Déclaration de fiche conseil';
      // keep all steps
    }
    this.currentStep = this.steps[0];

    this.deprecationWarningSub = this.conseilConfigService.deprecationWarningSubject.subscribe(isDeprecatedWarning => {
      if (isDeprecatedWarning) {
        this.ouiDialogService.openDialog(DeprecationWarningDialogComponent, {}, 'auto', '436px');
      }
    });
  }
  ngOnDestroy(): void {
    this.deprecationWarningSub?.unsubscribe();
    this.conseilConfigService.reset();
  }
  ngAfterViewInit() {
    this.currentStep = this.steps[0];
    this.tryInitOnExistingFiche();
    this.stepper?.currentStepIndexChange.subscribe(stepIndex => {
      let possibleStatesForTargetStep = this.steps[stepIndex].ficheStates;
      if (possibleStatesForTargetStep?.length > 0) {
        this.ficheConseilService.returnToPreviousState(
          possibleStatesForTargetStep[possibleStatesForTargetStep.length - 1]
        );
      }
    });
  }

  onContratSelected(contrat: Commande | null) {
    if (contrat) {
      const produit = {
        id: contrat.produit?.id,
        libelle: contrat.produit?.nom,
        partenaireId: contrat.produit?.idPartenaire,
        partenaireNom: contrat.produit?.idPartenaireNavigation?.nom,
      };
      this.ficheConseilService.onFicheConseilDataEdit('produit', produit);
      this.ficheConseilService.onFicheConseilDataEdit('contratId', contrat.id);
      this.ficheConseilService.onFicheConseilDataEdit('numeroContrat', contrat.numeroContrat);

      const invs = contrat?.investisseurCommande;
      const investisseur = {
        id: invs[0]?.investisseur?.id,
        nom: invs[0]?.investisseur?.investisseurEntite?.personnePhysique?.nom,
        prenom: invs[0]?.investisseur?.investisseurEntite?.personnePhysique?.prenom,
        displayName: invs[0]?.investisseur?.investisseurEntite?.displayName,
        code: invs[0]?.investisseur?.code,
        dateNaissance: invs[0]?.investisseur?.investisseurEntite?.personnePhysique?.dateNaissance,
        civiliteCode:
          invs[0]?.investisseur?.investisseurEntite?.personnePhysique?.genre === PERSONNE_PHYSIQUE_FEMALE_GENDER_VALUE
            ? 'MME'
            : 'MR',
        statutCodeNavigation: {
          libelle: invs[0]?.investisseur?.statutCodeNavigation?.libelle,
          code: invs[0]?.investisseur?.statutCodeNavigation?.code,
        },
        creationDate: invs[0]?.investisseur?.creationDate,
      };
      this.ficheConseilService.onFicheConseilDataEdit('investisseur', investisseur);

      if (invs.length > 1) {
        const coInvestisseur = {
          id: invs[1]?.investisseur?.id,
          nom: invs[1]?.investisseur?.investisseurEntite?.personnePhysique?.nom,
          prenom: invs[1]?.investisseur?.investisseurEntite?.personnePhysique?.prenom,
          displayName: invs[1]?.investisseur?.investisseurEntite?.displayName,
          code: invs[1]?.investisseur?.code,
          dateNaissance: invs[1]?.investisseur?.investisseurEntite?.personnePhysique?.dateNaissance,
          civiliteCode:
            invs[1]?.investisseur?.investisseurEntite?.personnePhysique?.genre === PERSONNE_PHYSIQUE_FEMALE_GENDER_VALUE
              ? 'MME'
              : 'MR',
          statutCodeNavigation: {
            libelle: invs[1]?.investisseur?.statutCodeNavigation?.libelle,
            code: invs[1]?.investisseur?.statutCodeNavigation?.code,
          },
          creationDate: invs[1]?.investisseur?.creationDate,
        };
        this.ficheConseilService.onFicheConseilDataEdit('coInvestisseur', coInvestisseur);
      }
    }
  }

  tryInitOnExistingFiche() {
    const currentFicheId = this.route.snapshot.paramMap.get('ficheId');
    this.isLoading = true;
    if (currentFicheId && this.isGuid(currentFicheId)) {
      this.ficheConseilService.onFicheIdSelected(currentFicheId);
      this.ficheConseilService.currentFicheConseilSubject.subscribe(currentFiche => {
        if (currentFiche) {
          this.currentFicheConseil = currentFiche;
          this.operationTypeLabel = this.currentFicheConseil.natureOperation?.libelle ?? 'type operation inconnu';
          this.enveloppeLabel = this.currentFicheConseil.enveloppe?.libelle ?? 'enveloppe inconnue';
          this.InvestisseurTypeLabel =
            this.currentFicheConseil.investisseurType === InvestisseurType.PersonneMorale
              ? 'Personne morale'
              : 'Personne physique';
          const msInDay = 1000 * 60 * 60 * 24;
          this.ficheConseilSuppressionDelay = Math.ceil(
            Math.abs(
              new Date().getTime() - (new Date(this.currentFicheConseil.dateModification).getTime() + 90 * msInDay)
            ) / msInDay
          );
          this.isLoading = false;
          this.updateStateStepper();
        }
      });
    }
  }

  private isGuid(value: string): boolean {
    const regexExp = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;
    return regexExp.test(value);
  }

  private updateStateStepper() {
    let ficheState = this.currentFicheConseil?.statut as FicheConseilState;

    //if should update state
    if (!this.currentStep?.ficheStates.includes(ficheState)) {
      // compute new step index
      let stepperIndex = 0;
      for (let index = 0; index < this.steps.length; index++) {
        const step = this.steps[index];
        if (step.ficheStates.includes(ficheState)) {
          stepperIndex = index;
        }
      }

      // set new step index
      this.stepper?.setStep(stepperIndex);
      this.currentStep = this.steps[stepperIndex];
    }

    // update stepper status
    let currentStepIndex = this.stepper?.stepIndex;
    if (currentStepIndex !== undefined) {
      for (let index = 0; index < this.steps.length; index++) {
        if (index < currentStepIndex && this.stepper?.steps[index]) {
          this.stepper.steps[index].state = 'done';
          this.stepper.steps[index].icon = 'done';
        } else if (this.stepper?.steps[index]) {
          this.stepper.steps[index].state = 'todo';
          this.stepper.steps[index].icon = '';
        }
      }
    }
  }

  isStepValid(step: FicheStep): boolean {
    switch (step.stepEnum) {
      case StepEnum.CONTRAT:
        return this.contratStepComponent?.isStepValid() ?? false;
      case StepEnum.INVESTISSEUR:
        return this.investisseurStepComponent?.isStepValid() ?? false;
      case StepEnum.EXIGENCES:
        return this.exigencesStepComponent?.isStepValid() ?? false;
      case StepEnum.CONNAISSANCE:
        return this.stepConnaissanceClientComponent?.isStepValid() ?? false;
      case StepEnum.INVESTISSEMENT:
        return this.projetStepComponent?.isStepValid() ?? false;
      case StepEnum.PRECISIONS:
        return this.precisionComplementaireStep?.isStepValid() ?? false;
      default:
        return false;
    }
  }

  markStepAsTouched(step?: FicheStep): void {
    switch (step?.stepEnum) {
      case StepEnum.CONTRAT:
        return this.contratStepComponent?.markAsTouched();
      case StepEnum.INVESTISSEUR:
        return this.investisseurStepComponent?.markAsTouched();
      case StepEnum.EXIGENCES:
        return this.exigencesStepComponent?.markAsTouched();
      case StepEnum.CONNAISSANCE:
        return this.stepConnaissanceClientComponent?.markAsTouched();
      case StepEnum.INVESTISSEMENT:
        return this.projetStepComponent?.markAsTouched();
      case StepEnum.PRECISIONS:
        return this.precisionComplementaireStep?.markAsTouched();
      default:
        return;
    }
  }

  //#region footer
  nextStepButtonActivity(): boolean {
    return !!this.currentStep && this.isStepValid(this.currentStep);
  }

  async onNextStepClick() {
    // on fige la valeur des status lors de l'état initial car ils peuvent changer au cours du traitement
    const isButtonActive = this.nextStepButtonActivity();
    const isLastStep = this.isLastStep;
    if (isButtonActive) {
      const isStepChangedOk = await this.ficheConseilService.lastStateOrChangeToNextState();
      if (isStepChangedOk && isLastStep) {
        const ficheId = this.currentFicheConseil?.id;
        this.router.navigate(['../fiches'], { queryParams: { ficheId } });
      }
      return;
    } else {
      this.snackbarService.open(
        'Veuillez corriger les erreurs ou remplir les champs obligatoires manquants',
        'error',
        5000,
        {
          horizontal: 'left',
          vertical: 'bottom',
        }
      );

      this.markStepAsTouched(this.currentStep);
    }
  }

  /**
   * Saves the current fiche and exits the wizard.
   * If `generatePDF` is true, navigates to the fiche list page with the PDF to generate as a query param.
   * @param generatePDF Whether to generate a PDF before exiting.
   */
  saveAndExit() {
    this.ficheConseilService.pushFicheConseilDataToBackEnd().then(hasBeenSaved => {
      if (hasBeenSaved) {
        this.ficheConseilService.resetCurrentFicheConseil();
        this.currentFicheConseil = null;
        this.router.navigate(['../fiches']);
      } else {
        this.snackbarService.open('Une erreur est survenue lors de la sauvegarde de la fiche', 'error', 5000, {
          horizontal: 'left',
          vertical: 'bottom',
        });
      }
    });
  }

  onSaveAndExitClik() {
    this.saveAndExit();
  }
  onPreviousStepClick() {
    if (this.currentStep === this.stepper?.steps[0] || this.currentStep?.stepEnum === StepEnum.CONTRAT) {
      this.router.navigate(['fiches']);
      return;
    } else {
      this.stepper?.prevStep();
    }
  }
  //#endregion footer
}
